import { useCallback } from 'react';
import { isNativeApp } from '@/lib/capacitor';

type HapticStyle = 'light' | 'medium' | 'heavy' | 'success' | 'warning' | 'error' | 'selection';

/**
 * useHaptic — small wrapper around @capacitor/haptics with a safe web fallback.
 *
 * On native (iOS/Android) it triggers the appropriate vibration via Capacitor.
 * On web it falls back to navigator.vibrate when available, otherwise no-op.
 *
 * Usage:
 *   const haptic = useHaptic();
 *   <button onClick={() => { haptic('light'); doStuff(); }} />
 */
export function useHaptic() {
  return useCallback(async (style: HapticStyle = 'light') => {
    try {
      if (isNativeApp()) {
        const { Haptics, ImpactStyle, NotificationType } = await import('@capacitor/haptics');
        switch (style) {
          case 'light':
            return Haptics.impact({ style: ImpactStyle.Light });
          case 'medium':
            return Haptics.impact({ style: ImpactStyle.Medium });
          case 'heavy':
            return Haptics.impact({ style: ImpactStyle.Heavy });
          case 'success':
            return Haptics.notification({ type: NotificationType.Success });
          case 'warning':
            return Haptics.notification({ type: NotificationType.Warning });
          case 'error':
            return Haptics.notification({ type: NotificationType.Error });
          case 'selection':
            return Haptics.selectionStart();
        }
      } else if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
        const ms = style === 'heavy' ? 25 : style === 'medium' ? 15 : 10;
        navigator.vibrate(ms);
      }
    } catch {
      // Silently ignore — haptics are non-critical.
    }
  }, []);
}
